import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import {
	CategoryRounded,
	DashboardRounded,
	DescriptionRounded,
	DirectionsRunRounded,
	FilterAltRounded,
	Inventory2Rounded,
	PaletteRounded,
	ShoppingBagRounded,
	TimelineRounded,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";

const palette = {
	ink: "#111110",
	ink2: "#343432",
	ink3: "#6b6b68",
	ink4: "#9b9b97",
	surface: "#fafaf8",
	surface2: "#f2f1ee",
	surface3: "#e8e7e2",
	border: "rgba(0,0,0,.08)",
	border2: "rgba(0,0,0,.14)",
	sidebar: "#141412",
	sidebarText: "#c8c8c4",
	sidebarMuted: "#70706b",
	accent: "#c8a96e",
	green: "#15803d",
	greenBg: "#dcfce7",
	amber: "#b45309",
	amberBg: "#fef3c7",
	red: "#b91c1c",
	redBg: "#fee2e2",
	blue: "#2563eb",
	blueBg: "#dbeafe",
	purple: "#7c3aed",
	purpleBg: "#ede9fe",
};

const brands = [
	{ key: "all", label: "All brands", total: 1284, color: palette.accent },
	{ key: "Rare Rabbit", label: "Rare Rabbit", total: 412, color: "#111110" },
	{ key: "Rareism", label: "Rareism", total: 341, color: "#7c3aed" },
	{ key: "Rareones", label: "Rareones", total: 218, color: "#d97706" },
	{ key: "Rarez", label: "Rarez", total: 313, color: "#0284c7" },
];

const navItems = [
	{ key: "dashboard", label: "Dashboard", icon: <DashboardRounded /> },
	{ key: "otb", label: "Open to Buy", icon: <Inventory2Rounded /> },
	{ key: "plan", label: "Buying Plan", icon: <CalendarIcon /> },
	{ key: "po", label: "PO Tracker", icon: <TimelineRounded /> },
	{ key: "matrix", label: "Style × Color × Size", icon: <CategoryRounded /> },
	{ key: "warehouse", label: "Warehouse", icon: <Inventory2Rounded /> },
	{ key: "store", label: "Store Buying", icon: <ShoppingBagRounded /> },
	{ key: "margin", label: "Margin", icon: <DirectionsRunRounded /> },
	{ key: "ai", label: "AI Ideas", icon: <PaletteRounded /> },
];

const stylesData = [
	{
		code: "RR-2479",
		name: "Oxford Shirt",
		brand: "Rare Rabbit",
		category: "Apparel",
		warehouse: "Jigani",
		season: "SS25",
		planned: 620,
		bought: 420,
		otb: 200,
		mrp: 1540000,
		cog: 640000,
		sold: 320,
		ros: 12,
		cover: 48,
	},
	{
		code: "RR-2511",
		name: "Classic Pin Belt",
		brand: "Rare Rabbit",
		category: "Belts",
		warehouse: "Gurugram",
		season: "SS25",
		planned: 180,
		bought: 150,
		otb: 30,
		mrp: 195000,
		cog: 70500,
		sold: 118,
		ros: 18,
		cover: 28,
	},
	{
		code: "RM-1140",
		name: "Floral Wrap Dress",
		brand: "Rareism",
		category: "Apparel",
		warehouse: "Maharashtra",
		season: "SS25",
		planned: 540,
		bought: 350,
		otb: 190,
		mrp: 1049000,
		cog: 385000,
		sold: 270,
		ros: 15,
		cover: 52,
	},
	{
		code: "RM-1109",
		name: "Structured Tote",
		brand: "Rareism",
		category: "Bags",
		warehouse: "Jigani",
		season: "SS25",
		planned: 240,
		bought: 150,
		otb: 90,
		mrp: 824850,
		cog: 315000,
		sold: 108,
		ros: 10,
		cover: 36,
	},
	{
		code: "RO-0391",
		name: "Kids Jogger",
		brand: "Rareones",
		category: "Apparel",
		warehouse: "West Bengal",
		season: "SS25",
		planned: 380,
		bought: 260,
		otb: 120,
		mrp: 493740,
		cog: 182000,
		sold: 190,
		ros: 11,
		cover: 44,
	},
	{
		code: "RO-0424",
		name: "Travel Duffel",
		brand: "Rareones",
		category: "Bags",
		warehouse: "Gurugram",
		season: "SS25",
		planned: 190,
		bought: 120,
		otb: 70,
		mrp: 599880,
		cog: 228000,
		sold: 75,
		ros: 7,
		cover: 33,
	},
	{
		code: "RZ-0840",
		name: "Runner Pro",
		brand: "Rarez",
		category: "Footwear",
		warehouse: "Maharashtra",
		season: "SS25",
		planned: 760,
		bought: 510,
		otb: 250,
		mrp: 3059490,
		cog: 1224000,
		sold: 410,
		ros: 19,
		cover: 55,
	},
	{
		code: "RZ-0822",
		name: "Cabin Trolley",
		brand: "Rarez",
		category: "Trolley",
		warehouse: "Jigani",
		season: "SS25",
		planned: 120,
		bought: 75,
		otb: 45,
		mrp: 674925,
		cog: 262500,
		sold: 44,
		ros: 6,
		cover: 31,
	},
];

const monthlyPlan = [
	["May", "Linen / Shirts", 3200, 7040000, "Locked"],
	["Jun", "Footwear / Travel", 3980, 9560000, "Locked"],
	["Jul", "Festive Women", 4700, 11800000, "Planning"],
	["Aug", "Kids / Accessories", 5450, 13080000, "Planning"],
	["Sep", "Winter Prep", 6100, 15860000, "Upcoming"],
];

const poRows = [
	["PO-1001", "Vendor A", "Rare Rabbit", "Apparel", 2400, 5420000, "12 Aug", "Approved", false],
	["PO-1002", "Vendor B", "Rareism", "Bags", 1200, 2480000, "14 Aug", "In Production", true],
	["PO-1003", "Vendor C", "Rarez", "Footwear", 1800, 6250000, "19 Aug", "Shipped", false],
	["PO-1004", "Vendor D", "Rareones", "Apparel", 900, 1660000, "22 Aug", "Received", false],
];

const storeRows = [
	["Bengaluru - Indiranagar", "Bengaluru", "A", "High ROS", "JIG", 220, 680000, "18", "Increase depth"],
	["Delhi - Select City", "Delhi", "A", "Stable", "GGN", 180, 560000, "12", "Maintain"],
	["Chandigarh - Elante", "Chandigarh", "B", "High ROS", "WBG", 140, 420000, "16", "Increase depth"],
	["Ahmedabad - CG Road", "Ahmedabad", "B", "Slow", "MHR", 95, 240000, "8", "Reduce"],
];

const aiIdeas = [
	[
		"Double down on Navy & Black depth",
		"Navy and Black show stronger sell-through across core apparel. Increase OTB release for high ROS stores.",
		"92",
	],
	[
		"Freeze over-utilized OTB lines",
		"Categories above 85% utilization should require approval before more buy orders are released.",
		"88",
	],
	[
		"Correct M/L size curve",
		"M and L are under-depth in multiple apparel options. Shift XS/XXL buy into M/L for next intake.",
		"84",
	],
	[
		"Rebalance WH stock before allocation",
		"Warehouse score shows imbalance. Transfer part of Jigani stock before store push.",
		"79",
	],
	[
		"Improve Trolley margin",
		"Trolley COG is high versus portfolio target. Consolidate vendors or reduce low-margin variants.",
		"76",
	],
	["Increase Tier-2 buying selectively", "Ahmedabad and Chandigarh stores have lower allocation than footfall potential.", "82"],
];

function CalendarIcon() {
	return <DescriptionRounded />;
}

function metricSx() {
	return {
		bgcolor: "#fff",
		border: `1px solid ${palette.border}`,
		borderRadius: 2,
		p: 1.75,
		boxShadow: "none",
	};
}

function cardSx() {
	return {
		bgcolor: "#fff",
		border: `1px solid ${palette.border}`,
		borderRadius: 2,
		p: 2,
		boxShadow: "none",
	};
}

function metricValue(value) {
	return typeof value === "number" ? value.toLocaleString("en-IN") : value;
}

function sumRows(rows) {
	return rows.reduce(
		(acc, row) => {
			acc.planned += row.planned;
			acc.bought += row.bought;
			acc.otb += row.otb;
			acc.mrp += row.mrp;
			acc.cog += row.cog;
			acc.sold += row.sold;
			acc.cover += row.cover;
			return acc;
		},
		{ planned: 0, bought: 0, otb: 0, mrp: 0, cog: 0, sold: 0, cover: 0 },
	);
}

function BarRow({ label, value, pct, color, valueWidth = 58, labelWidth = 96 }) {
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
			<Typography sx={{ fontSize: 11, color: palette.ink3, width: labelWidth, flexShrink: 0 }}>{label}</Typography>
			<Box sx={{ flex: 1, height: 7, bgcolor: palette.surface2, borderRadius: 999, overflow: "hidden" }}>
				<Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, borderRadius: 999 }} />
			</Box>
			<Typography
				sx={{
					width: valueWidth,
					textAlign: "right",
					fontSize: 11,
					color: palette.ink2,
					fontFamily: "Roboto, sans-serif",
					fontWeight: 500,
				}}>
				{value}
			</Typography>
		</Box>
	);
}

const MrpReport = () => {
	const [activePage, setActivePage] = useState("dashboard");
	const [activeBrand, setActiveBrand] = useState("all");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [seasonFilter, setSeasonFilter] = useState("SS25");
	const [warehouseFilter, setWarehouseFilter] = useState("");
	const [searchText, setSearchText] = useState("");
	const [matrixMode, setMatrixMode] = useState("qty");

	const filterOptions = useMemo(() => {
		const brandOptions = ["all", ...new Set(stylesData.map((row) => row.brand))];
		const categoryOptions = [...new Set(stylesData.map((row) => row.category))];
		const warehouseOptions = [...new Set(stylesData.map((row) => row.warehouse))];
		const seasonOptions = [...new Set(stylesData.map((row) => row.season))];
		return { brandOptions, categoryOptions, warehouseOptions, seasonOptions };
	}, []);

	const filteredRows = useMemo(() => {
		return stylesData.filter((row) => {
			if (activeBrand !== "all" && row.brand !== activeBrand) return false;
			if (categoryFilter && row.category !== categoryFilter) return false;
			if (seasonFilter && row.season !== seasonFilter) return false;
			if (warehouseFilter && row.warehouse !== warehouseFilter) return false;
			if (searchText) {
				const q = searchText.toLowerCase();
				const haystack = `${row.code} ${row.name} ${row.brand} ${row.category} ${row.warehouse}`.toLowerCase();
				if (!haystack.includes(q)) return false;
			}
			return true;
		});
	}, [activeBrand, categoryFilter, seasonFilter, warehouseFilter, searchText]);

	const totals = useMemo(() => sumRows(filteredRows), [filteredRows]);
	const sellThrough = totals.bought ? Math.round((totals.sold / totals.bought) * 100) : 0;
	const gm = totals.mrp ? Math.round(((totals.mrp - totals.cog) / totals.mrp) * 100) : 0;
	const coverDays = filteredRows.length ? Math.round(totals.cover / filteredRows.length) : 0;

	const brandSummary = useMemo(() => {
		return filterOptions.brandOptions
			.filter((brand) => brand !== "all")
			.map((brand) => {
				const rows = filteredRows.filter((row) => row.brand === brand);
				const agg = sumRows(rows);
				return {
					brand,
					rows: rows.length,
					util: agg.planned ? Math.round((agg.bought / agg.planned) * 100) : 0,
					mrp: agg.mrp,
				};
			});
	}, [filterOptions.brandOptions, filteredRows]);

	const categories = useMemo(() => [...new Set(stylesData.map((row) => row.category))], []);

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: { xs: "1fr", lg: "232px 1fr" },
				minHeight: "calc(100vh - 170px)",
				border: `1px solid ${palette.border}`,
				borderRadius: 2,
				overflow: "hidden",
				bgcolor: palette.surface,
				color: palette.ink,
				fontFamily: "Roboto, sans-serif",
			}}>
			<Box
				sx={{
					bgcolor: grey[900],
					color: palette.sidebarText,
					p: 2,
					display: "flex",
					flexDirection: "column",
					gap: 2,
					borderRight: { xs: "none", lg: "1px solid rgba(255,255,255,0.06)" },
				}}>
				<Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.07)", pb: 1.5 }}>
					<Typography
						sx={{
							fontSize: 9,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
							color: palette.sidebarMuted,
							mb: 1.25,
						}}>
						Buying Views
					</Typography>
					<Stack spacing={0.35}>
						{navItems.map((item) => (
							<Box
								key={item.key}
								onClick={() => setActivePage(item.key)}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									p: 1,
									borderRadius: 1,
									cursor: "pointer",
									bgcolor: activePage === item.key ? "rgba(200,169,110,0.15)" : "transparent",
									color: activePage === item.key ? palette.accent : "white",
									"&:hover": { bgcolor: "rgba(255,255,255,0.05)", color: palette.sidebarText },
								}}>
								<Box sx={{ display: "inline-flex", alignItems: "center", fontSize: 16 }}>{item.icon}</Box>
								<Typography sx={{ fontSize: 12 }}>{item.label}</Typography>
							</Box>
						))}
					</Stack>
				</Box>

				<Box sx={{ flex: 1 }}>
					<Typography
						sx={{
							fontSize: 9,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
							color: palette.sidebarMuted,
							mb: 1.25,
						}}>
						Brands
					</Typography>
					<Stack spacing={0.25}>
						{brands.map((brand) => (
							<Box
								key={brand.key}
								onClick={() => setActiveBrand(brand.key)}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									p: 1,
									borderRadius: 1,
									cursor: "pointer",
									bgcolor: activeBrand === brand.key ? "rgba(255,255,255,0.07)" : "transparent",
									"&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
								}}>
								<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: brand.color, flexShrink: 0 }} />
								<Typography sx={{ fontSize: 12, color: palette.sidebarText }}>{brand.label}</Typography>
								<Typography sx={{ ml: "auto", fontSize: 10, fontFamily: "Roboto, sans-serif", color: "white" }}>
									{brand.total.toLocaleString("en-IN")}
								</Typography>
							</Box>
						))}
					</Stack>
				</Box>

				<Box sx={{ pt: 2, borderTop: "1px solid rgba(255,255,255,0.07)", fontSize: 10, color: palette.sidebarMuted }}>
					Season {seasonFilter} · Updated now
				</Box>
			</Box>

			<Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1320 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
					<Box>
						<Typography sx={{ fontSize: 29, fontWeight: 700, color: palette.ink, lineHeight: 1.1 }}>
							Buying Module
						</Typography>
						<Typography sx={{ fontSize: 12, color: palette.ink3, mt: 0.5 }}>
							OTB · PO · Style depth · Warehouse · Store allocation · Margin · AI recommendations
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
						gap: 1.25,
						mb: 2,
					}}>
					<Box sx={metricSx()}>
						<Typography
							sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
							Total MRP bought
						</Typography>
						<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{metricValue(totals.mrp)}</Typography>
						<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>
							{metricValue(totals.bought)} units
						</Typography>
					</Box>
					<Box sx={metricSx()}>
						<Typography
							sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
							Total COG
						</Typography>
						<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{metricValue(totals.cog)}</Typography>
						<Typography sx={{ fontSize: 10, color: palette.green, mt: 0.75 }}>GM {gm}%</Typography>
					</Box>
					<Box sx={metricSx()}>
						<Typography
							sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
							OTB Remaining
						</Typography>
						<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{metricValue(totals.otb * 2200)}</Typography>
						<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>
							{metricValue(totals.otb)} units open
						</Typography>
					</Box>
					<Box sx={metricSx()}>
						<Typography
							sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
							Sell-through
						</Typography>
						<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{sellThrough}%</Typography>
						<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>sold / bought</Typography>
					</Box>
					<Box sx={metricSx()}>
						<Typography
							sx={{ fontSize: 10, color: palette.ink3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
							Depth / Option
						</Typography>
						<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
							{filteredRows.length ? Math.round(totals.bought / filteredRows.length) : 0}
						</Typography>
						<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>avg bought qty</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr))" },
						gap: 1,
						p: 1.5,
						mb: 2,
						border: `1px solid ${palette.border}`,
						bgcolor: "#fff",
						borderRadius: 2,
						position: "sticky",
						top: 0,
						zIndex: 2,
					}}>
					<Chip
						icon={<FilterAltRounded />}
						label="Global Filters"
						size="small"
						sx={{ justifySelf: "start", bgcolor: palette.surface2 }}
					/>
					<FormControl size="small">
						<InputLabel>Brand</InputLabel>
						<Select label="Brand" value={activeBrand} onChange={(e) => setActiveBrand(e.target.value)}>
							{filterOptions.brandOptions.map((brand) => (
								<MenuItem key={brand} value={brand}>
									{brand === "all" ? "All brands" : brand}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small">
						<InputLabel>Category</InputLabel>
						<Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
							<MenuItem value="">All categories</MenuItem>
							{filterOptions.categoryOptions.map((cat) => (
								<MenuItem key={cat} value={cat}>
									{cat}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl size="small">
						<InputLabel>Season</InputLabel>
						<Select label="Season" value={seasonFilter} onChange={(e) => setSeasonFilter(e.target.value)}>
							{filterOptions.seasonOptions.map((season) => (
								<MenuItem key={season} value={season}>
									{season}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						size="small"
						label="Search"
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						placeholder="Search style / store"
					/>
				</Box>

				{activePage === "dashboard" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
									<Typography sx={{ fontSize: 12, fontWeight: 600 }}>OTB utilization by brand</Typography>
									<Typography sx={{ fontSize: 10, color: palette.ink4 }}>planned vs bought</Typography>
								</Box>
								{brandSummary.map((row) => (
									<BarRow
										key={row.brand}
										label={row.brand}
										value={`${row.util}%`}
										pct={row.util}
										color={row.util > 85 ? palette.red : row.util > 70 ? palette.amber : palette.green}
									/>
								))}
							</Box>

							<Box sx={cardSx()}>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
									<Typography sx={{ fontSize: 12, fontWeight: 600 }}>PO pipeline</Typography>
									<Typography sx={{ fontSize: 10, color: palette.ink4 }}>workflow control</Typography>
								</Box>
								{[
									["Created", 1, 14, "#9b9b97"],
									["Approved", 1, 14, "#6366f1"],
									["In Production", 1, 14, "#d97706"],
									["Shipped", 1, 14, "#0284c7"],
									["Received", 1, 14, "#059669"],
								].map(([label, value, pct, color]) => (
									<BarRow key={label} label={label} value={value} pct={pct} color={color} />
								))}
							</Box>
						</Box>

						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.5 }}>Top buying risks</Typography>
								<Stack spacing={1}>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											p: 1.2,
											border: `1px solid ${palette.border}`,
											borderRadius: 1.25,
										}}>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: 1,
												bgcolor: palette.redBg,
												color: palette.red,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											!
										</Box>
										<Box>
											<Typography sx={{ fontSize: 12, fontWeight: 600 }}>POs flagged at risk</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4 }}>
												Vendor delay / ETA risk needs follow-up.
											</Typography>
										</Box>
									</Box>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											p: 1.2,
											border: `1px solid ${palette.border}`,
											borderRadius: 1.25,
										}}>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: 1,
												bgcolor: palette.amberBg,
												color: palette.amber,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											S
										</Box>
										<Box>
											<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
												M &amp; L under-depth visible
											</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4 }}>
												Apparel size curves need stronger planning.
											</Typography>
										</Box>
									</Box>
								</Stack>
							</Box>

							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.5 }}>Top opportunities</Typography>
								<Stack spacing={1}>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											p: 1.2,
											border: `1px solid ${palette.border}`,
											borderRadius: 1.25,
										}}>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: 1,
												bgcolor: palette.greenBg,
												color: palette.green,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											+
										</Box>
										<Box>
											<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
												Navy and Black are hero colors
											</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4 }}>
												Increase depth in Rare Rabbit and Rarez.
											</Typography>
										</Box>
									</Box>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											p: 1.2,
											border: `1px solid ${palette.border}`,
											borderRadius: 1.25,
										}}>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: 1,
												bgcolor: palette.blueBg,
												color: palette.blue,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											%
										</Box>
										<Box>
											<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
												Accessories margin is attractive
											</Typography>
											<Typography sx={{ fontSize: 10, color: palette.ink4 }}>
												Low COG and high GM makes it a quick top-up.
											</Typography>
										</Box>
									</Box>
								</Stack>
							</Box>

							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.5 }}>Category health</Typography>
								{categories.map((category) => {
									const rows = filteredRows.filter((row) => row.category === category);
									const agg = sumRows(rows);
									const sell = agg.bought ? Math.round((agg.sold / agg.bought) * 100) : 0;
									return (
										<BarRow
											key={category}
											label={category}
											value={`${sell}%`}
											pct={sell}
											color={sell > 50 ? palette.green : sell > 35 ? palette.amber : palette.red}
										/>
									);
								})}
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "otb" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1.75 }}>
							{brandSummary.map((brand) => (
								<Box key={brand.brand} sx={cardSx()}>
									<Typography sx={{ fontSize: 12, fontWeight: 600 }}>{brand.brand}</Typography>
									<Typography sx={{ fontSize: 11, color: palette.ink4, mt: 0.4 }}>
										OTB utilization by brand
									</Typography>
									<Box sx={{ mt: 1.5 }}>
										<BarRow
											label="Utilization"
											value={`${brand.util}%`}
											pct={brand.util}
											color={brand.util > 85 ? palette.red : brand.util > 70 ? palette.amber : palette.green}
										/>
									</Box>
									<Typography sx={{ fontSize: 11, color: palette.ink4 }}>MRP: {metricValue(brand.mrp)}</Typography>
								</Box>
							))}
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Category-wise OTB table</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "left",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th">Category</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Planned
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Bought
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											OTB
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											MRP
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Util %
										</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{categories.map((category) => {
										const rows = filteredRows.filter((row) => row.category === category);
										const agg = sumRows(rows);
										const util = agg.planned ? Math.round((agg.bought / agg.planned) * 100) : 0;
										return (
											<Box
												component="tr"
												key={category}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">{category}</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.planned)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.bought)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.otb)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.mrp)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{util}%
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "plan" && (
					<Stack spacing={2.25}>
						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Monthly intake calendar</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "left",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th">Month</Box>
										<Box component="th">Focus</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Planned Units
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Budget
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Status
										</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{monthlyPlan.map((row) => (
										<Box
											component="tr"
											key={row[0]}
											sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
											<Box component="td">{row[0]}</Box>
											<Box component="td">{row[1]}</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												{metricValue(row[2])}
											</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												{metricValue(row[3])}
											</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												<Chip
													size="small"
													label={row[4]}
													sx={{
														height: 20,
														fontSize: 10,
														bgcolor:
															row[4] === "Locked"
																? palette.greenBg
																: row[4] === "Planning"
																	? palette.amberBg
																	: palette.blueBg,
													}}
												/>
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Recommended buying plan</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "left",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th">Category</Box>
										<Box component="th">Recommendation</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Qty
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Budget
										</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{categories.map((category) => {
										const rows = filteredRows.filter((row) => row.category === category);
										const agg = sumRows(rows);
										const qty = Math.max(150, Math.round(agg.otb * 0.85));
										return (
											<Box
												component="tr"
												key={category}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">{category}</Box>
												<Box component="td">{qty > agg.otb ? "Increase depth" : "Controlled buy"}</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(qty)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(qty * 2200)}
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "po" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>PO stage tracker</Typography>
								{["Created", "Approved", "In Production", "Shipped", "Received"].map((stage, index) => (
									<BarRow
										key={stage}
										label={stage}
										value={index + 1}
										pct={14 + index * 16}
										color={["#9b9b97", "#6366f1", "#d97706", "#0284c7", "#059669"][index]}
									/>
								))}
							</Box>
							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Vendor status</Typography>
								{["Vendor A", "Vendor B", "Vendor C", "Vendor D"].map((vendor, index) => (
									<BarRow
										key={vendor}
										label={vendor}
										value={`${20 - index} PO`}
										pct={70 - index * 12}
										color={index === 1 ? palette.amber : index === 2 ? palette.blue : palette.green}
									/>
								))}
							</Box>
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Purchase order details</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "left",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th">PO</Box>
										<Box component="th">Vendor</Box>
										<Box component="th">Brand</Box>
										<Box component="th">Category</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Units
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Value
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											ETA
										</Box>
										<Box component="th" sx={{ textAlign: "right" }}>
											Status
										</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{poRows.map((row) => (
										<Box
											component="tr"
											key={row[0]}
											sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
											<Box component="td" sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}>
												{row[0]}
											</Box>
											<Box component="td">{row[1]}</Box>
											<Box component="td">{row[2]}</Box>
											<Box component="td">{row[3]}</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												{metricValue(row[4])}
											</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												{metricValue(row[5])}
											</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												{row[6]}
											</Box>
											<Box component="td" sx={{ textAlign: "right" }}>
												<Chip
													size="small"
													label={row[7]}
													sx={{
														height: 20,
														fontSize: 10,
														bgcolor:
															row[7] === "Received"
																? palette.greenBg
																: row[7] === "Shipped"
																	? palette.blueBg
																	: row[7] === "In Production"
																		? palette.amberBg
																		: palette.surface2,
													}}
												/>
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "matrix" && (
					<Stack spacing={2.25}>
						<ToggleButtonGroup
							value={matrixMode}
							exclusive
							onChange={(_, next) => next && setMatrixMode(next)}
							size="small"
							sx={{
								"& .MuiToggleButton-root": {
									textTransform: "none",
									borderColor: palette.border2,
									color: palette.ink3,
								},
								"& .Mui-selected": { bgcolor: `${palette.ink} !important`, color: "#fff !important" },
							}}>
							<ToggleButton value="qty">Qty Heatmap</ToggleButton>
							<ToggleButton value="missing">Missing Sizes</ToggleButton>
							<ToggleButton value="depth">Depth Health</ToggleButton>
						</ToggleButtonGroup>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Style × Color × Size</Typography>
							{matrixMode === "qty" && (
								<Box sx={{ overflowX: "auto" }}>
									<Box component="table" sx={{ borderCollapse: "collapse", minWidth: 900, width: "100%" }}>
										<Box component="thead">
											<Box
												component="tr"
												sx={{
													"& th": {
														fontSize: 10,
														color: palette.ink4,
														borderBottom: `1px solid ${palette.border}`,
														textAlign: "center",
														py: 1,
													},
												}}>
												<Box component="th" sx={{ textAlign: "left" }}>
													Style
												</Box>
												<Box component="th">Brand</Box>
												<Box component="th">Color</Box>
												{["XS", "S", "M", "L", "XL", "XXL"].map((size) => (
													<Box component="th" key={size}>
														{size}
													</Box>
												))}
												<Box component="th">Total</Box>
												<Box component="th">ROS</Box>
												<Box component="th">Cover</Box>
											</Box>
										</Box>
										<Box component="tbody">
											{filteredRows.map((row) => (
												<Box
													component="tr"
													key={row.code}
													sx={{
														"& td": {
															fontSize: 10,
															borderBottom: `1px solid ${palette.border}`,
															py: 0.9,
															textAlign: "center",
														},
													}}>
													<Box component="td" sx={{ textAlign: "left" }}>
														<Box sx={{ fontWeight: 600, fontSize: 11 }}>{row.name}</Box>
														<Box
															sx={{
																fontSize: 9,
																color: palette.ink4,
																fontFamily: "Roboto, sans-serif",
															}}>
															{row.code}
														</Box>
													</Box>
													<Box component="td">{row.brand}</Box>
													<Box component="td">{row.category}</Box>
													{["XS", "S", "M", "L", "XL", "XXL"].map((size, index) => (
														<Box
															component="td"
															key={size}
															sx={{
																bgcolor:
																	index === 2
																		? palette.blueBg
																		: index === 3
																			? palette.greenBg
																			: palette.surface,
																fontWeight: 500,
															}}>
															{Math.max(0, Math.round((row.bought / 6) * (index === 2 ? 1.2 : 1)))}
														</Box>
													))}
													<Box component="td" sx={{ fontWeight: 600 }}>
														{metricValue(row.bought)}
													</Box>
													<Box component="td">{row.ros}</Box>
													<Box component="td">{row.cover}d</Box>
												</Box>
											))}
										</Box>
									</Box>
								</Box>
							)}

							{matrixMode === "missing" && (
								<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
									<Box component="thead">
										<Box
											component="tr"
											sx={{
												"& th": {
													fontSize: 10,
													color: palette.ink4,
													textAlign: "left",
													borderBottom: `1px solid ${palette.border}`,
													pb: 1,
												},
											}}>
											<Box component="th">Style</Box>
											<Box component="th">Brand</Box>
											<Box component="th">Color</Box>
											<Box component="th">Missing / Weak Sizes</Box>
											<Box component="th">Action</Box>
										</Box>
									</Box>
									<Box component="tbody">
										{filteredRows.map((row) => (
											<Box
												component="tr"
												key={row.code}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">
													<Box sx={{ fontWeight: 600 }}>{row.name}</Box>
													<Box sx={{ fontSize: 9, color: palette.ink4 }}>{row.code}</Box>
												</Box>
												<Box component="td">{row.brand}</Box>
												<Box component="td">{row.category}</Box>
												<Box component="td">
													<Chip
														size="small"
														label={row.cover > 45 ? "Weak M/L" : "Balanced"}
														sx={{
															height: 20,
															fontSize: 10,
															bgcolor: row.cover > 45 ? palette.amberBg : palette.greenBg,
														}}
													/>
												</Box>
												<Box component="td">{row.cover > 45 ? "Top-up required" : "No action"}</Box>
											</Box>
										))}
									</Box>
								</Box>
							)}

							{matrixMode === "depth" && (
								<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
									<Box component="thead">
										<Box
											component="tr"
											sx={{
												"& th": {
													fontSize: 10,
													color: palette.ink4,
													textAlign: "left",
													borderBottom: `1px solid ${palette.border}`,
													pb: 1,
												},
											}}>
											<Box component="th">Style</Box>
											<Box component="th">Brand</Box>
											<Box component="th" sx={{ textAlign: "right" }}>
												Bought
											</Box>
											<Box component="th" sx={{ textAlign: "right" }}>
												Sold
											</Box>
											<Box component="th" sx={{ textAlign: "right" }}>
												Cover Days
											</Box>
											<Box component="th">Depth Health</Box>
										</Box>
									</Box>
									<Box component="tbody">
										{filteredRows.map((row) => (
											<Box
												component="tr"
												key={row.code}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">
													<Box sx={{ fontWeight: 600 }}>{row.name}</Box>
													<Box sx={{ fontSize: 9, color: palette.ink4 }}>{row.code}</Box>
												</Box>
												<Box component="td">{row.brand}</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(row.bought)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(row.sold)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{row.cover}
												</Box>
												<Box component="td">
													<Chip
														size="small"
														label={row.cover > 80 ? "Over-depth" : row.cover > 45 ? "Watch" : "Healthy"}
														sx={{
															height: 20,
															fontSize: 10,
															bgcolor:
																row.cover > 80
																	? palette.redBg
																	: row.cover > 45
																		? palette.amberBg
																		: palette.greenBg,
														}}
													/>
												</Box>
											</Box>
										))}
									</Box>
								</Box>
							)}
						</Box>
					</Stack>
				)}

				{activePage === "warehouse" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
							{["Jigani", "Gurugram", "West Bengal", "Maharashtra"].map((wh, index) => {
								const value = filteredRows
									.filter((row) => row.warehouse === wh)
									.reduce((acc, row) => acc + row.bought, 0);
								return (
									<Box key={wh} sx={metricSx()}>
										<Typography
											sx={{
												fontSize: 10,
												color: palette.ink3,
												textTransform: "uppercase",
												letterSpacing: "0.06em",
												mb: 1,
											}}>
											{wh}
										</Typography>
										<Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
											{metricValue(value)}
										</Typography>
										<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>Units</Typography>
									</Box>
								);
							})}
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Warehouse × Category matrix</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "right",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th" sx={{ textAlign: "left" }}>
											Category
										</Box>
										{["Jigani", "Gurugram", "West Bengal", "Maharashtra"].map((wh) => (
											<Box component="th" key={wh}>
												{wh}
											</Box>
										))}
										<Box component="th">Total</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{categories.map((category) => {
										const catRows = filteredRows.filter((row) => row.category === category);
										const vals = ["Jigani", "Gurugram", "West Bengal", "Maharashtra"].map((wh) =>
											catRows.filter((row) => row.warehouse === wh).reduce((acc, row) => acc + row.bought, 0),
										);
										return (
											<Box
												component="tr"
												key={category}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">{category}</Box>
												{vals.map((value, idx) => (
													<Box component="td" key={idx} sx={{ textAlign: "right" }}>
														{metricValue(value)}
													</Box>
												))}
												<Box component="td" sx={{ textAlign: "right", fontWeight: 600 }}>
													{metricValue(vals.reduce((a, b) => a + b, 0))}
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "store" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Store buying summary</Typography>
								{storeRows.map((row) => (
									<BarRow
										key={row[0]}
										label={row[0]}
										value={row[7]}
										pct={row[7] === "18" ? 72 : row[7] === "12" ? 60 : row[7] === "16" ? 66 : 48}
										color={row[7] === "18" ? palette.green : row[7] === "16" ? palette.blue : palette.amber}
									/>
								))}
							</Box>
							<Box sx={cardSx()}>
								<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Store cluster distribution</Typography>
								{["High ROS", "Stable", "Slow"].map((cluster, index) => (
									<BarRow
										key={cluster}
										label={cluster}
										value={index === 0 ? 2 : 1}
										pct={index === 0 ? 66 : index === 1 ? 45 : 30}
										color={index === 0 ? palette.green : index === 1 ? palette.blue : palette.amber}
									/>
								))}
							</Box>
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Allocation recommendations</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "left",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th">Store</Box>
										<Box component="th">Recommended Action</Box>
										<Box component="th">Category Focus</Box>
										<Box component="th">Reason</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{storeRows.map((row) => (
										<Box
											component="tr"
											key={row[0]}
											sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
											<Box component="td">{row[0]}</Box>
											<Box component="td">
												<Chip
													size="small"
													label={row[8]}
													sx={{
														height: 20,
														fontSize: 10,
														bgcolor:
															row[8] === "Increase depth"
																? palette.greenBg
																: row[8] === "Reduce"
																	? palette.amberBg
																	: palette.blueBg,
													}}
												/>
											</Box>
											<Box component="td">
												{row[1] === "Delhi"
													? "Rare Rabbit Apparel"
													: row[1] === "Bengaluru"
														? "Rare Rabbit Apparel"
														: "Core styles"}
											</Box>
											<Box component="td">
												{row[8] === "Increase depth"
													? "High ROS and good sell-through"
													: "Stock cover and cluster fit"}
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "margin" && (
					<Stack spacing={2.25}>
						<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(6, 1fr)" }, gap: 1.25 }}>
							{[
								["MRP", metricValue(totals.mrp), "gross value"],
								["COG", metricValue(totals.cog), "cost value"],
								["Gross Margin", `${gm}%`, "portfolio margin"],
								["Markdown Risk", coverDays > 55 ? "High" : coverDays > 40 ? "Medium" : "Low", "based on cover"],
								["Stock Cover", `${coverDays}d`, "current inventory"],
								["Full Price Potential", sellThrough > 35 ? "Good" : "Watch", "sell-through signal"],
							].map(([label, value, sub]) => (
								<Box key={label} sx={metricSx()}>
									<Typography
										sx={{
											fontSize: 10,
											color: palette.ink3,
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											mb: 1,
										}}>
										{label}
									</Typography>
									<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
									<Typography sx={{ fontSize: 10, color: palette.ink3, mt: 0.75 }}>{sub}</Typography>
								</Box>
							))}
						</Box>

						<Box sx={cardSx()}>
							<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.25 }}>Category profitability</Typography>
							<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
								<Box component="thead">
									<Box
										component="tr"
										sx={{
											"& th": {
												fontSize: 10,
												color: palette.ink4,
												textAlign: "right",
												borderBottom: `1px solid ${palette.border}`,
												pb: 1,
											},
										}}>
										<Box component="th" sx={{ textAlign: "left" }}>
											Category
										</Box>
										<Box component="th">MRP</Box>
										<Box component="th">COG</Box>
										<Box component="th">GM%</Box>
										<Box component="th">Sell-through</Box>
										<Box component="th">Profitability</Box>
									</Box>
								</Box>
								<Box component="tbody">
									{categories.map((category) => {
										const rows = filteredRows.filter((row) => row.category === category);
										const agg = sumRows(rows);
										const gmPct = agg.mrp ? Math.round(((agg.mrp - agg.cog) / agg.mrp) * 100) : 0;
										const sellPct = agg.bought ? Math.round((agg.sold / agg.bought) * 100) : 0;
										return (
											<Box
												component="tr"
												key={category}
												sx={{ "& td": { fontSize: 11, py: 1, borderBottom: `1px solid ${palette.border}` } }}>
												<Box component="td">{category}</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.mrp)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{metricValue(agg.cog)}
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{gmPct}%
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													{sellPct}%
												</Box>
												<Box component="td" sx={{ textAlign: "right" }}>
													<Chip
														size="small"
														label={gmPct > 62 ? "Strong" : gmPct > 55 ? "Medium" : "Low"}
														sx={{
															height: 20,
															fontSize: 10,
															bgcolor:
																gmPct > 62
																	? palette.greenBg
																	: gmPct > 55
																		? palette.amberBg
																		: palette.redBg,
														}}
													/>
												</Box>
											</Box>
										);
									})}
								</Box>
							</Box>
						</Box>
					</Stack>
				)}

				{activePage === "ai" && (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
							gap: 1.75,
						}}>
						{aiIdeas.map((idea, index) => (
							<Box
								key={idea[0]}
								sx={{
									...cardSx(),
									borderLeft: `3px solid ${[palette.blue, palette.amber, palette.purple, palette.green, palette.red, "#0284c7"][index]}`,
								}}>
								<Box
									sx={{
										width: 32,
										height: 32,
										borderRadius: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										mb: 1.25,
										bgcolor: palette.surface2,
									}}>
									Auto
								</Box>
								<Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{idea[0]}</Typography>
								<Typography sx={{ fontSize: 11, color: palette.ink3, lineHeight: 1.6 }}>{idea[1]}</Typography>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25 }}>
									<Chip size="small" label={idea[2]} sx={{ height: 20, fontSize: 10, bgcolor: palette.blueBg }} />
									<Typography sx={{ fontSize: 10, color: palette.ink4 }}>{idea[2]}% confidence</Typography>
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default MrpReport;
